import { UploadScenarios } from 'interfaces/file-uploader';
import { uploadDimensions } from 'config/upload-dimensions';
import {
  uploadConfig,
  imageExtensions,
  videoExtensions,
  documentExtensions,
} from './file-uploader';

export const formatFileSize = (sizeInBytes: number): string => {
  const mb = sizeInBytes / 1000000;
  if (mb >= 1000) {
    return `${(mb / 1000).toFixed(1)}GB`;
  }
  return `${Math.round(mb)}MB`;
};

export const formatFileExtensions = (extensions: string[]): string => extensions
  .map((ext) => ext.replace('.', '').toUpperCase())
  .join(', ');

export const getFileTypeDescription = (extensions: string[]): string => {
  const hasImages = extensions.some((ext) => imageExtensions.includes(ext as any));
  const hasVideos = extensions.some((ext) => videoExtensions.includes(ext as any));
  const hasDocs = extensions.some((ext) => documentExtensions.includes(ext as any));

  const types = [];
  if (hasImages) types.push('Images');
  if (hasVideos) types.push('Videos');
  if (hasDocs) types.push('Documents');

  return types.join(', ') || 'Files';
};

export const getUploadRequirements = (scenario: UploadScenarios) => {
  const config = uploadConfig.get(scenario);
  const dimensions = uploadDimensions.get(scenario);

  if (!config) return null;

  const maxSize = formatFileSize(config.sizeLimit);
  const extensions = formatFileExtensions(config.allowedExtensions);
  const fileTypeDesc = getFileTypeDescription(config.allowedExtensions);

  let sizeText = `Max: ${maxSize}`;
  if (config.sizeLimitsByType) {
    const typeLimits = Object.entries(config.sizeLimitsByType)
      .map(([type, limit]) => `${type}: ${formatFileSize(limit)}`)
      .join(', ');
    sizeText = `Max: ${maxSize} (${typeLimits})`;
  }

  let aspectRatioText = '';
  if (dimensions?.aspectRatio) {
    aspectRatioText = ` Use an image with a ${dimensions.aspectRatio} aspect ratio and minimum dimensions of ${dimensions.width}x${dimensions.height} px for optimal fit.`;
  }

  return {
    maxSize,
    extensions,
    fileTypeDesc,
    dimensions: dimensions ? `${dimensions.width}x${dimensions.height}px` : null,
    dimensionDescription: dimensions?.description,
    requirementsText: `${sizeText} | ${extensions} only`,
    recommendedText: aspectRatioText || (dimensions ? `Recommended: ${dimensions.width}x${dimensions.height}px` : null),
  };
};
